import { withStyles } from '@material-ui/core';
import { styled } from "@material-ui/styles";
import { useIO, useLinked } from '@type-r/react';
import { AgGridReact } from 'ag-grid-react';
import { CollapsableSection, Column, Columns, Flex, FormSelect, Input, Loading, Option } from 'lqx-react';
import { FormLabel } from 'lqx-react/src/forms/horizontal';
import React, { useCallback, useEffect, useRef } from "react";
import NumberFormat from 'react-number-format';
import { hasRole } from 'src/app/services/can';
import { Company, getCompanies } from '../../api/company';
import { getBuyersAsCommonParticipants, getPayeesAsCommonParticipants, getPayersAsCommonParticipants, getSuppliersAsCommonParticipants } from '../../api/companyParticipant';
import { ManualRemittanceAdvice } from "../../api/manualRemittanceAdvice";
import { CounterParty, RAInvoice, RemittanceAdvice } from '../../api/remittanceAdvice';
import { BuyerMultiSelectDropdownEditor, DateEditor, NumberWithDecimalEditor, SelectCurrencyEditor, SupplierMultiSelectDropdownEditor } from '../../components/ag-grid-cell-formating';
import { AgGridReactContainer, StyledAddCircle, StyledRemoveCircle } from '../../components/ag-grid-styles';
import { DigiDateWrapper } from '../../components/date-input';
import { AssetColumn, AssetLabel, StyledFormLabel, numberInputStyles } from '../../components/number-input';
import { DigiAsyncSelect, DigiCurrencySelect } from '../../components/select';
import { MultiSelectDropdown } from '../../connections/components/multiselect-dropdown';
import { DigiDateInput, ErrorHighligter, asTimelessLinkedDate } from '../../shared';
import { RemittanceAdviceInvoiceTableColumns } from './remittance-advice-invoice-table-columns';
import { globalStore } from 'api/store';
import { DigiSelectAsync } from '../../components/select-async';

export const RemittanceAdviceForm = ({ remittanceAdvice, confirmDataStep = false }: { remittanceAdvice: RemittanceAdvice | ManualRemittanceAdvice, confirmDataStep?}) => {
    const classes = numberInputStyles();
    const defaultColDef = {
        editable: true,
        filter: false,
        resizable: true,
        minWidth: 150
    };
    const gridApi = useRef(null);
    const onGridReady = (params: { api: any; }) => {
        gridApi.current = params.api;
    };
    const onAddRow = useCallback(() => {
        remittanceAdvice.invoices.unshift(new RAInvoice());
        gridApi.current!.setRowData(remittanceAdvice.invoices.models);
        gridApi.current!.setFocusedCell(0, 'number');
        gridApi.current!.startEditingCell({ rowIndex: 0, colKey: 'number' });
    }, [])
    const onRemoveRow = useCallback(() => {
        const selectedData = gridApi.current!.getSelectedRows();
        remittanceAdvice.invoices.remove(selectedData);
        gridApi.current.updateRowData({ remove: selectedData });
    }, []);

    const isReady = useIO(
        async () => {
            if (remittanceAdvice.dataHolderId) {
                $dataHolder.set((await getCompanies(null, remittanceAdvice.dataHolderId)).items[0]);
            }
        }, []);

    const $dataHolder = useLinked<Company>(new Company())
        .onChange(s => {
            if (s && s.id) {
                remittanceAdvice.dataHolderId = Number(s.id);
            }
        });

    const getBankAcountInformation = (counterParty: CounterParty, counterPartyName: string, getCommonParticipants: any) => {
        const counterPartyNameLoverCase = counterPartyName.toLowerCase();
        return <>
            <Flex row>
                <Column>
                    <StyledFormLabel>{counterPartyName} </StyledFormLabel>
                    <ErrorHighligter error={remittanceAdvice.errors[`${counterPartyNameLoverCase}.Company.Name`] || remittanceAdvice.warnings[`${counterPartyNameLoverCase}`]}>
                        <DigiAsyncSelect
                            aria-label="Company Counter Party"
                            data-qa-id={`company ${counterPartyNameLoverCase}`}
                            getItems={getCommonParticipants}
                            $value={counterParty.$.company}
                            isCreater={hasRole('AssetFacilitator')}
                        />
                    </ErrorHighligter>
                </Column>
            </Flex>
            <br />
            <CollapsableSection title="Bank Account Information" noMargins>
                <Flex row>
                    <Column>
                        <StyledFormLabel>Name</StyledFormLabel>
                        <ErrorHighligter error={remittanceAdvice.errors[`${counterPartyNameLoverCase}.BankName`]}>
                            <Input data-qa-id={`name ${counterPartyNameLoverCase}`} $value={counterParty.$.bankName} aria-label="Name" />
                        </ErrorHighligter>
                    </Column>
                    <Column>
                        <StyledFormLabel>Alias</StyledFormLabel>
                        <ErrorHighligter error={remittanceAdvice.errors[`${counterPartyNameLoverCase}.BankAlias`]}>
                            <Input data-qa-id={`alias ${counterPartyNameLoverCase}`} $value={counterParty.$.bankAlias} aria-label="Alias" />
                        </ErrorHighligter>
                    </Column>
                </Flex>
                <br />
                <Flex row>
                    <Column>
                        <StyledFormLabel>Account Number</StyledFormLabel>
                        <ErrorHighligter error={remittanceAdvice.errors[`${counterPartyNameLoverCase}.BankAccountNumber`]}>
                            <Input data-qa-id={`account number ${counterPartyNameLoverCase}`} $value={counterParty.$.bankAccountNumber} aria-label="Account Number" />
                        </ErrorHighligter>
                    </Column>
                    <Column>
                        <StyledFormLabel>Branch Code</StyledFormLabel>
                        <ErrorHighligter error={remittanceAdvice.errors[`${counterPartyNameLoverCase}.BankBranchCode`]}>
                            <Input data-qa-id={`branch code ${counterPartyNameLoverCase}`} $value={counterParty.$.bankBranchCode} aria-label="Branch Code" />
                        </ErrorHighligter>
                    </Column>
                </Flex>
            </CollapsableSection>
        </>
    }

    return isReady ? <>
        {hasRole('Administrator') &&
            <>
                <Flex row>
                    <Column>
                        <StyledFormLabel>Data Owner</StyledFormLabel>
                        <DigiSelectAsync
                            data-qa-id={`data owner`}
                            getItems={getCompanies}
                            $value={$dataHolder}
                        />
                    </Column>
                </Flex>
                <br />
            </>
        }
        {getBankAcountInformation(remittanceAdvice.payor, 'Payor', getPayersAsCommonParticipants)}
        {getBankAcountInformation(remittanceAdvice.payee, 'Payee', getPayeesAsCommonParticipants)}
        <Flex row>
            <Column>
                <StyledFormLabel>Payment Date</StyledFormLabel>
                <ErrorHighligter error={remittanceAdvice.errors['paymentDate']}>
                    <DigiDateInput data-qa-id="payment date" date $value={asTimelessLinkedDate(remittanceAdvice.$.paymentDate)} />
                </ErrorHighligter>
            </Column>
            <Column>
                <StyledFormLabel>Payment Currency</StyledFormLabel>
                <ErrorHighligter error={remittanceAdvice.errors['paymentCurrency']}>
                    <DigiCurrencySelect
                        aria-label="Payment Currency"
                        isMulti={false}
                        options={globalStore.currenciesLookup.items}
                        $value={remittanceAdvice.$.paymentCurrency}
                    />
                </ErrorHighligter>
            </Column>
            <Column>
                <StyledFormLabel>Payment Amount</StyledFormLabel>
                <ErrorHighligter error={remittanceAdvice.errors['paymentAmount']}>
                    <NumberFormat
                        aria-label="Payment Amount"
                        data-qa-id="paymentAmount"
                        value={remittanceAdvice.paymentAmount}
                        className={classes.numericInput}
                        decimalScale={2}
                        fixedDecimalScale={true}
                        thousandSeparator={true}
                        onValueChange={(values) => {
                            const { floatValue } = values;
                            remittanceAdvice.$.paymentAmount.set(floatValue)
                        }} />
                </ErrorHighligter>
            </Column>
        </Flex>
        <br />
        <Flex row>
            <Column>
                <StyledFormLabel>Payment Reference Number</StyledFormLabel>
                <ErrorHighligter error={remittanceAdvice.errors['paymentReferenceNumber']}>
                    <Input data-qa-id="payment reference number" $value={remittanceAdvice.$.paymentReferenceNumber} aria-label="Payment Reference Number" />
                </ErrorHighligter>
            </Column>
            <Column>
                <StyledFormLabel>Relationship Id</StyledFormLabel>
                <ErrorHighligter error={remittanceAdvice.errors['relationShipId']}>
                    <Input data-qa-id="relationship id" $value={remittanceAdvice.$.relationShipId} aria-label="Relationship Id" />
                </ErrorHighligter>
            </Column>
        </Flex>
        <br />
        <CollapsableSection title="Invoice">
            {confirmDataStep && remittanceAdvice.invoice ?
                <>
                    <Columns>
                        <AssetColumn>
                            <AssetLabel>Invoice Number *</AssetLabel>
                            <ErrorHighligter error={remittanceAdvice.errors['invoice.Number']}>
                                <Input data-qa-id="invoice number" $value={remittanceAdvice.invoice.$.number} aria-label="Invoice Number" />
                            </ErrorHighligter>
                        </AssetColumn>
                        <AssetColumn>
                            <AssetLabel>Invoice Date</AssetLabel>
                            <ErrorHighligter error={remittanceAdvice.errors['invoice.InvoiceDate']}>
                                <DigiDateWrapper>
                                    <DigiDateInput data-qa-id="invoice date" date $value={asTimelessLinkedDate(remittanceAdvice.invoice.$.invoiceDate)} />
                                </DigiDateWrapper>
                            </ErrorHighligter>
                        </AssetColumn>
                        <AssetColumn>
                            <AssetLabel>Invoice Due Date</AssetLabel>
                            <ErrorHighligter error={remittanceAdvice.errors['invoice.InvoiceDueDate']}>
                                <DigiDateWrapper>
                                    <DigiDateInput data-qa-id="invoice due date" date $value={asTimelessLinkedDate(remittanceAdvice.invoice.$.invoiceDueDate)} />
                                </DigiDateWrapper>
                            </ErrorHighligter>
                        </AssetColumn>
                        <AssetColumn>
                            <AssetLabel>Face Value</AssetLabel>
                            <ErrorHighligter error={remittanceAdvice.errors['invoice.FaceValue']}>
                                <NumberFormat data-qa-id="face value" value={remittanceAdvice.invoice.faceValue} className={classes.numericInput} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; remittanceAdvice.invoice.$.faceValue.set(floatValue) }} aria-label="Face Value" />
                            </ErrorHighligter>
                        </AssetColumn>
                        <AssetColumn>
                            <AssetLabel>Currency</AssetLabel>
                            <ErrorHighligter error={remittanceAdvice.errors['invoice.Currency']}>
                                <DigiCurrencySelect
                                    aria-label="Currency"
                                    isMulti={false}
                                    options={globalStore.currenciesLookup.items.models}
                                    $value={remittanceAdvice.invoice.$.currency}
                                />
                            </ErrorHighligter>
                        </AssetColumn>
                        <AssetColumn>
                            <AssetLabel>Discount</AssetLabel>
                            <ErrorHighligter error={remittanceAdvice.errors['invoice.Discount']}>
                                <NumberFormat data-qa-id="discount" value={remittanceAdvice.invoice.discount} className={classes.numericInput} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; remittanceAdvice.invoice.$.discount.set(floatValue) }} aria-label="Discount" />
                            </ErrorHighligter>
                        </AssetColumn>
                        <AssetColumn>
                            <AssetLabel>Net Amount</AssetLabel>
                            <ErrorHighligter error={remittanceAdvice.errors['invoice.NetAmount']}>
                                <NumberFormat data-qa-id="net amount" value={remittanceAdvice.invoice.netAmount} className={classes.numericInput} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; remittanceAdvice.invoice.$.netAmount.set(floatValue) }} aria-label="Net Maount" />
                            </ErrorHighligter>
                        </AssetColumn>
                        <AssetColumn>
                            <AssetLabel>Applied Amount</AssetLabel>
                            <ErrorHighligter error={remittanceAdvice.errors['invoice.AppliedAmount']}>
                                <NumberFormat data-qa-id="applied amount" value={remittanceAdvice.invoice.appliedAmount} className={classes.numericInput} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; remittanceAdvice.invoice.$.appliedAmount.set(floatValue) }} aria-label="Applied Amount" />
                            </ErrorHighligter>
                        </AssetColumn>
                        <AssetColumn>
                            <AssetLabel>Write Off</AssetLabel>
                            <ErrorHighligter error={remittanceAdvice.errors['invoice.WriteOff']}>
                                <NumberFormat data-qa-id="write off" value={remittanceAdvice.invoice.writeOff} className={classes.numericInput} decimalScale={2} fixedDecimalScale={true} thousandSeparator={true} onValueChange={(values) => { const { floatValue } = values; remittanceAdvice.invoice.$.writeOff.set(floatValue) }} aria-label="Write Off" />
                            </ErrorHighligter>
                        </AssetColumn>
                        <AssetColumn>
                            <AssetLabel>Details</AssetLabel>
                            <ErrorHighligter error={remittanceAdvice.errors['invoice.Details']}>
                                <Input data-qa-id="details" $value={remittanceAdvice.invoice.$.details} aria-label="Details" />
                            </ErrorHighligter>
                        </AssetColumn>
                    </Columns>
                    <Columns>
                        <AssetColumn>
                            <AssetLabel>Supplier</AssetLabel>
                            <ErrorHighligter error={remittanceAdvice.errors['invoice.Supplier.Name'] || remittanceAdvice.warnings['invoice.Supplier']}>
                                <DigiAsyncSelect
                                    aria-label="Supplier"
                                    data-qa-id={`supplier`}
                                    getItems={getSuppliersAsCommonParticipants}
                                    $value={remittanceAdvice.invoice.$.supplier}
                                    isCreater={hasRole('AssetFacilitator')}
                                />
                            </ErrorHighligter>
                        </AssetColumn>
                        <AssetColumn>
                            <AssetLabel>Buyer</AssetLabel>
                            <ErrorHighligter error={remittanceAdvice.errors['invoice.Buyer.Name'] || remittanceAdvice.warnings['invoice.Buyer']}>
                                <DigiAsyncSelect
                                    aria-label="Buyer"
                                    data-qa-id={`buyer`}
                                    getItems={getBuyersAsCommonParticipants}
                                    $value={remittanceAdvice.invoice.$.buyer}
                                    isCreater={hasRole('AssetFacilitator')}
                                />
                            </ErrorHighligter>
                        </AssetColumn>
                    </Columns>
                </> :
                <>
                    <Section row>
                        <AddNewLine row>
                            <StyledAddCircle onClick={onAddRow}></StyledAddCircle>
                            <FormLabel>Add New Line</FormLabel>
                        </AddNewLine>
                        <RemoveLine row>
                            <StyledRemoveCircle onClick={onRemoveRow}></StyledRemoveCircle>
                            <FormLabel>Remove Line</FormLabel>
                        </RemoveLine>
                    </Section>
                    <AgGridReactContainerStyled className={`ag-theme-balham`}>
                        <AgGridReact
                            onGridReady={onGridReady}
                            columnDefs={RemittanceAdviceInvoiceTableColumns}
                            frameworkComponents={{ NumberWithDecimalEditor, DateEditor, SelectCurrencyEditor, SupplierMultiSelectDropdownEditor, BuyerMultiSelectDropdownEditor }}
                            rowSelection='multiple'
                            defaultColDef={defaultColDef}
                            overlayNoRowsTemplate={"No Items"}
                            rowData={remittanceAdvice.invoices as any}
                        >
                        </AgGridReact>
                    </AgGridReactContainerStyled>
                </>
            }
        </CollapsableSection>
    </> : <Loading />;
}

const FormSelectContainer = withStyles({
    root: {
        marginTop: 4,
        marginBottom: 0,
    },
    selectInput: {
        padding: 5.9
    }
})(FormSelect);

const Section = styled(Flex)({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: '0 0 5px 0'
})

const AddNewLine = styled(Flex)({
    width: '125px'
})

const RemoveLine = styled(Flex)({
    width: '116px'
})

const AgGridReactContainerStyled = styled(AgGridReactContainer)({
    height: '210px',
    margin: '0 16px 0 0'
})